import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Box, Button, MUIDataTable, MUIDataTableColumnDef, Pill, Progress } from 'web-library';
import {
  Coordinator,
  usePostAdminAppealCoordinatorSearch,
} from '../../../../api-refactored/appeal/usePostAdminAppealCoordinatorSearch';
import {
  usePostAdminAppealSendEmail
} from '../../../../api-refactored/appeal/usePostAdminAppealSendEmail';
import { KeyValueResponse } from '../../../../api-refactored/typings';
import FormHeader from '../../../../components/form-items/FormHeader';
import EligibleSchoolOptions
  from '../../../../pages/uniqueLink/AdminUniqueLink/EligibleSchoolOptions';
import { useUniqueLink } from '../../../../pages/uniqueLink/AdminUniqueLink/logic/useUniqueLink';

const useStyles = makeStyles()((theme) =>
({
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  contentContainer: {
    marginTop: theme.spacing(4),
  },
  viewDetailsButton: {
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    whiteSpace: 'nowrap',
    height: theme.spacing(3),
  },
  loadingIndicatorContainer: {
    marginLeft: theme.spacing(5),
  },
}));

/** define Mui DataTable Column Definition
 * */
const tableColumns = (
  classes: any,
  t: TFunction,
  coordinatorList: Coordinator[],
  setEditDetail: (name: string, uuid: string, index: number, useModal?: boolean) => void,
  sendEmailStatusList: string[],
  isEnableCreate: boolean
): MUIDataTableColumnDef[] => [
    {
      options: {
        searchable: true,
        sort: false,
      },
      name: 'coordinatorName',
      label: t('admin->>UniqueLink.CoordinatorName'),
    },
    {
      options: {
        searchable: true,
        sort: false,
      },
      name: 'email',
      label: t('admin->>UniqueLink.Email'),
    },
    {
      name: 'school',
      label: t('admin->>UniqueLink.School'),
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'action',
      label: t('admin->>UniqueLink.Action'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          let component;
          if (!sendEmailStatusList[dataIndex] || sendEmailStatusList[dataIndex] !== 'success') {
            if (sendEmailStatusList[dataIndex] === 'loading') {
              component = (
                <div className={classes.loadingIndicatorContainer}>
                  <Progress color="secondary" size="M" />
                </div>
              );
            } else {
              component = (
                <Button
                  className={classes.viewDetailsButton}
                  color="primary"
                  variant="text"
                  size="small"
                  onClick={() => {
                    setEditDetail(
                      coordinatorList[dataIndex].coordinatorName,
                      coordinatorList[dataIndex].uuid,
                      dataIndex,
                      false
                    );
                  }}
                >
                  {t('admin->>UniqueLink.GrantAccess')}
                </Button>
              );
            }
          } else {
            component = (
              <Pill label={t('admin->>UniqueLink.EmailSent')} size="S" variant="positive" />
            );
          }
          return component;
        },
        sort: false,
        filter: false,
        display: `${isEnableCreate}`,
      },
    },
  ];

interface CoordinatorAppealAccessListProps {
  isEnableCreate: boolean;
}

const CoordinatorAppealAccessList = ({ isEnableCreate }: CoordinatorAppealAccessListProps) => {
  const [selectedOrganization, setSelectedOrganization] = useState<KeyValueResponse>();

  const {
    selectedUserInfo,
    setSelectedUserInfo,
    sendEmailStatusList,
    setSentEmailStatusList,
    apiEmailStatusCallback,
    setEditDetail,
  } = useUniqueLink();

  const { t } = useTranslation();
  const { classes } = useStyles();

  const { data: coordinatorSearchList, mutate, isLoading } = usePostAdminAppealCoordinatorSearch();

  const { mutate: sendUniqueLinkEmailMutate } = usePostAdminAppealSendEmail({
    onSuccess: () => {
      if (selectedUserInfo) {
        apiEmailStatusCallback(
          selectedUserInfo.index,
          'success',
          t('admin->>UniqueLink.EmailHasSent', { name: selectedUserInfo.name })
        );
      }
    },
    onError: (error) => {
      if (selectedUserInfo) {
        apiEmailStatusCallback(
          selectedUserInfo.index,
          'error',
          error.response ? error.response.data.message! : error.message
        );
      }
    },
  });

  useEffect(() => {
    if (selectedOrganization) {
      setSentEmailStatusList([]);
      mutate({ uuid: selectedOrganization.code });
    }
  }, [selectedOrganization, mutate, setSentEmailStatusList]);

  useEffect(() => {
    if (selectedUserInfo && selectedUserInfo.uuid) {
      sendUniqueLinkEmailMutate({
        isOfflinePayment: null,
        userType: 'COORDINATORS',
        uuid: selectedUserInfo.uuid,
        applicationType: 'APPEAL',
      });
      setSelectedUserInfo({
        uuid: '',
        index: 0,
        name: '',
      });
      apiEmailStatusCallback(selectedUserInfo.index, 'loading', 'Processing...');
    }
  }, [apiEmailStatusCallback, selectedUserInfo, sendUniqueLinkEmailMutate, setSelectedUserInfo]);

  return (
    <>
      <FormHeader title={t('admin->>UniqueLink.CoordinatorDetails')} />

      <Box className={classes.contentContainer}>
        <EligibleSchoolOptions
          key="school"
          value={selectedOrganization}
          onChange={(value) => {
            setSelectedOrganization(value);
          }}
          disabled={false}
        />
      </Box>

      <div className={classes.tableContainer}>
        <MUIDataTable
          key="private-center"
          isLoading={isLoading}
          columns={tableColumns(
            classes,
            t,
            coordinatorSearchList || [],
            setEditDetail,
            sendEmailStatusList,
            isEnableCreate
          )}
          data={coordinatorSearchList || []}
        />
      </div>
    </>
  );
};
export default CoordinatorAppealAccessList;
